/**
 * Política de Tratamiento de Datos Personales — Grooming ERP / Netcloud Networking.
 *
 * Basada en la Ley Estatutaria 1581 de 2012, el Decreto 1377 de 2013 y demás
 * normas concordantes (República de Colombia). Parte de la misma política de
 * CommerceAdmin, adaptada a lo que este sistema sí trata y aquel no: datos de
 * las mascotas, sus controles de salud y las estadías de hotel y daycare.
 *
 * Nota sobre las mascotas: los datos del animal (raza, tamaño, color, purga,
 * vacunas) NO son datos personales — un animal no es titular en el sentido de
 * la Ley 1581. Pero van siempre asociados a un cliente identificado, así que
 * en la práctica se protegen igual, y así se declara.
 *
 * Para actualizar contactos, NIT o dirección basta editar RESPONSABLE. Al
 * cambiar el contenido hay que subir POLITICA_VERSION y la fecha: la política
 * anterior sigue siendo la que se le comunicó a los titulares hasta entonces.
 */

export const RESPONSABLE = {
  nombre: 'Netcloud Networking',
  representante: 'Daniel Felipe Montaña Duque (persona natural)',
  nit: '1015425118-0',
  direccion: 'Carrera 22 # 20-25',
  ciudad: 'Colombia',
  emailContacto: 'soporte.ncn@netcloudnetworking.com',
  productoComercial: 'Grooming ERP',
};

export const POLITICA_VERSION = '1.0';
export const POLITICA_ACTUALIZADA = '2026-09-18';

export const POLITICA_SECCIONES: { id: string; titulo: string; cuerpo: string }[] = [
  {
    id: 'identificacion',
    titulo: '1. Identificación del Responsable del Tratamiento',
    cuerpo: `**${RESPONSABLE.nombre}** (${RESPONSABLE.representante}), identificado con NIT ${RESPONSABLE.nit}, con domicilio en ${RESPONSABLE.direccion}, ${RESPONSABLE.ciudad}, es el Responsable del Tratamiento de los Datos Personales que se recolectan, almacenan y procesan a través del software comercial **${RESPONSABLE.productoComercial}**.

Cuando el comerciante que usa el software recolecta datos de sus propios clientes o empleados, él actúa como Responsable y ${RESPONSABLE.nombre} como Encargado.

Cualquier consulta, petición, queja, reclamo o solicitud relacionada con la protección de datos personales puede dirigirse al correo electrónico **${RESPONSABLE.emailContacto}**.`,
  },
  {
    id: 'definiciones',
    titulo: '2. Definiciones',
    cuerpo: `Para efectos de esta política, se aplican las definiciones del artículo 3 de la Ley 1581 de 2012:

- **Titular**: persona natural cuyos datos personales son objeto de tratamiento.
- **Responsable del Tratamiento**: persona natural o jurídica que decide sobre el tratamiento de los datos.
- **Encargado del Tratamiento**: persona que realiza el tratamiento por cuenta del Responsable.
- **Dato personal**: cualquier información vinculada o que pueda asociarse a una persona natural determinada o determinable.
- **Dato sensible**: aquellos que afectan la intimidad del titular o cuyo uso indebido puede generar discriminación.
- **Tratamiento**: cualquier operación sobre datos personales (recolección, almacenamiento, uso, circulación, supresión).
- **Autorización**: consentimiento previo, expreso e informado del titular para el tratamiento de sus datos.`,
  },
  {
    id: 'finalidades',
    titulo: '3. Finalidades del Tratamiento',
    cuerpo: `Los datos personales recolectados a través de ${RESPONSABLE.productoComercial} se utilizan, por parte del comerciante y de ${RESPONSABLE.nombre} como Encargado, para:

- Gestión de la agenda y de la prestación del servicio: citas, servicios y adicionales realizados a cada mascota.
- Registro de la ficha de la mascota y de su historial de visitas, incluido el tipo de corte habitual.
- **Recordatorios de controles de salud de la mascota** (purga, pastilla de garrapatas y vacuna anual) y avisos de que hace tiempo que no asiste, enviados al teléfono que el cliente registró.
- Gestión de estadías de **hotel y daycare**: reservas, huéspedes, abonos y saldos.
- Cobro y facturación interna: ventas, medios de pago, abonos, cartera y cierre de caja.
- Control de inventario, compras a proveedores y gastos del establecimiento.
- Atención de solicitudes, peticiones, quejas o reclamos.
- Análisis estadístico interno para mejorar el servicio.
- Cumplimiento de las demás obligaciones legales aplicables al comerciante.

Los datos no se utilizan para finalidades distintas a las aquí descritas sin autorización previa del titular.`,
  },
  {
    id: 'datos-recolectados',
    titulo: '4. Información Objeto de Tratamiento',
    cuerpo: `Según el rol del titular dentro del sistema, los datos tratados pueden incluir:

**Clientes del establecimiento:**
- Nombres y apellidos
- Correo electrónico y teléfono (el teléfono se usa para los recordatorios)
- Dirección, barrio y municipio
- Categoría asignada por el establecimiento (nuevo, frecuente, VIP, premium o vetado)
- Historial de citas, consumos, abonos, saldos y pagos

**Mascotas del cliente** (no son datos personales, pero van unidos a un cliente identificado):
- Nombre, especie, raza, tamaño y color
- Fotografía de la mascota, cuando el cliente la aporta
- Tipo de corte habitual y notas de comportamiento
- Controles de salud: fecha de la última purga, pastilla de garrapatas y vacuna, con el producto aplicado

**Usuarios del sistema (personal del salón):**
- Nombres y apellidos, correo electrónico
- Rol, empresa y sede asignados
- Fecha del último ingreso

**Proveedores y sus representantes:**
- Razón social o nombre, NIT o documento, correo y teléfono
- Compras registradas y gastos asociados

**Archivos que se cargan al sistema:**
- Fotografías de mascotas
- Comprobantes de pago de la suscripción, que pueden contener datos bancarios
- Logotipo del establecimiento

**Sobre los datos sensibles.** Este sistema **no recolecta datos sensibles** en el sentido del artículo 5 de la Ley 1581 de 2012: no trata datos de salud de personas, ni datos biométricos, ni información sobre origen racial, orientación política, religiosa o sexual. Los datos de salud que se registran son los de la **mascota**, que no es titular de datos personales. La categoría del cliente (por ejemplo «vetado») es una anotación comercial del establecimiento sobre la prestación del servicio, no un dato sensible.`,
  },
  {
    id: 'derechos',
    titulo: '5. Derechos del Titular',
    cuerpo: `De conformidad con el artículo 8 de la Ley 1581 de 2012, el titular tiene derecho a:

- **Conocer, actualizar y rectificar** sus datos personales frente al Responsable o Encargado.
- **Solicitar prueba de la autorización** otorgada, salvo cuando la ley exceptúe ese requisito.
- **Ser informado** sobre el uso que se ha dado a sus datos, previa solicitud.
- **Presentar quejas** ante la Superintendencia de Industria y Comercio (SIC).
- **Revocar la autorización y solicitar la supresión** del dato cuando el tratamiento no respete los principios, derechos y garantías legales.
- **Acceder gratuitamente** a sus datos personales objeto de tratamiento.

La supresión no procede cuando exista un deber legal de conservar la información, como ocurre con los soportes contables de las ventas y compras, que deben conservarse por el término que fija la ley comercial.`,
  },
  {
    id: 'procedimiento',
    titulo: '6. Procedimiento para Ejercer los Derechos',
    cuerpo: `Para ejercer cualquiera de esos derechos, el titular puede:

1. Escribir a **${RESPONSABLE.emailContacto}** con el asunto "Habeas Data — [tipo de solicitud]".
2. Adjuntar copia del documento de identidad para validar la titularidad.
3. Describir con claridad los hechos y el derecho que desea ejercer.

**Tiempos de respuesta** (artículos 14 y 15 de la Ley 1581 de 2012):
- **Consultas**: máximo 10 días hábiles. Si no fuera posible, se informan los motivos y la fecha de atención, con máximo 5 días hábiles adicionales.
- **Reclamos**: máximo 15 días hábiles. Si el reclamo está incompleto, se requiere al interesado dentro de los 5 días siguientes.

Vencidos los términos sin respuesta, el titular puede acudir a la **Superintendencia de Industria y Comercio**, autoridad de protección de datos en Colombia.`,
  },
  {
    id: 'seguridad',
    titulo: '7. Medidas de Seguridad',
    cuerpo: `${RESPONSABLE.nombre} aplica las siguientes medidas técnicas y administrativas:

- **Cifrado en tránsito (HTTPS/TLS)** en toda comunicación con el sistema.
- **Control de acceso por roles**, con el principio de mínimo privilegio.
- **Aislamiento entre empresas** a nivel de base de datos, de modo que cada comerciante solo alcanza sus propios datos.
- **Contraseñas cifradas** y sesiones firmadas. ${RESPONSABLE.nombre} no puede consultar la contraseña de nadie: solo restablecerla.
- **Registro de accesos**: se conserva la fecha del último ingreso de cada usuario y un conteo de los módulos que utiliza, con fines de soporte y de mejora del servicio.
- Los documentos electrónicos se firman con **certificado digital** emitido a nombre del comerciante.

Ningún sistema es inviolable. Ante un incidente de seguridad que afecte datos personales, ${RESPONSABLE.nombre} notificará al comerciante afectado y, cuando proceda, a la SIC, conforme al artículo 17 literal n) de la Ley 1581 de 2012.`,
  },
  {
    id: 'transferencias',
    titulo: '8. Transferencias y Transmisiones',
    cuerpo: `Para prestar el servicio, los datos pueden ser tratados por terceros que actúan como Encargados:

- **Mercado Pago** (Colombia): procesamiento de los pagos de la suscripción, cuando se paga por ese medio.
- **Zoho Corporation**: envío de los correos con los documentos electrónicos.
- **Mercado Pago**: procesamiento de los pagos de la suscripción al software.
- **Meta Platforms**: envío de notificaciones por WhatsApp, cuando el comerciante activa esa función.
- **Hostinger**: proveedor de la infraestructura donde corren el sistema y la base de datos.
- **Google (Google Fonts)**: la interfaz carga sus tipografías desde los servidores de Google, lo que implica que Google recibe la dirección IP del navegador que abre el sistema. No se le envía ningún otro dato.

La infraestructura del sistema está alojada en servidores contratados por ${RESPONSABLE.nombre}, que pueden encontrarse fuera de Colombia. Los acuerdos con cada proveedor exigen estándares de seguridad equivalentes a los de la legislación colombiana.`,
  },
  {
    id: 'vigencia',
    titulo: '9. Vigencia',
    cuerpo: `Esta política rige desde su publicación. Los datos se conservan durante el tiempo necesario para cumplir las finalidades autorizadas y, después, durante los términos que exigen las normas tributarias, comerciales y laborales — en particular los **cinco años** de conservación de los documentos electrónicos y sus soportes.

**Versión**: ${POLITICA_VERSION}
**Última actualización**: ${POLITICA_ACTUALIZADA}

${RESPONSABLE.nombre} puede modificar esta política. Los cambios se comunican dentro de ${RESPONSABLE.productoComercial} con al menos diez (10) días hábiles de anticipación, salvo que obedezcan a un requerimiento legal.`,
  },
];

export const TEXTO_AUTORIZACION = `Autorizo a ${RESPONSABLE.nombre} y al comerciante a tratar mis datos personales conforme a la Política de Tratamiento de Datos Personales, para las finalidades descritas (gestión comercial, facturación electrónica y comunicaciones del servicio).`;
